/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './DashboardTable.scss';
import 'style/components/MobileTable.scss';

import classnames from 'classnames';
import {
  string,
  object,
  bool,
  array,
  number,
  oneOfType,
  arrayOf,
  element
} from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { isActivePlus, isBrokerage } from 'services/Products';

import Toggle from 'components/shared/ToggleRP';

import { Dollar } from 'components/shared/Text/Text';

import { metaDataTypes, orderAccountTableKeys } from 'services/TableSupport';

import {
  valueCells,
  labelCells
} from 'components/shared/Table/MobileTableSupport';

import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

import {
  flow,
  isNil,
  isPlainObject,
  isString,
  get,
  kebabCase,
  omit,
  omitBy,
  reduce,
  sortBy,
  startCase
} from 'lodash';

import IntradayRefresh from 'components/shared/Intraday/IntradayRefresh';
import { intradayRefreshEnabled } from 'services/FeatureFlags';
import { getIntradayRefreshEnabled } from 'state/account/AccountSelectors';
import { intradayEligibleFilter } from 'services/Accounts';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';

const getTypeName = (account) =>
  get(
    account,
    'details.name.accountType.type',
    get(account, 'details.name.accountType', '')
  );

const COL_LABEL_ACCOUNT = 'account';
const COL_LABEL_BALANCE = 'balance';

const defaultColumns = [COL_LABEL_ACCOUNT, COL_LABEL_BALANCE];

const TableColumns = ({ columns }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={`mobile-dashboard-table-column-${kebabCase(
          name
        )} e2e-mobile-dashboard-table-column-${kebabCase(
          name
        )} column-header-cell`}
        key={name}>
        {startCase(name)}
      </th>
    ))}
  </tr>
);

TableColumns.propTypes = {
  columns: arrayOf(oneOfType([element, string]))
};

const GroupLabelOwners = ({ owners }) => (
  <div className="mobile-dashboard-table-account-group-label-owners">
    {owners}
  </div>
);

const GroupLabelOwnersWithPermissions = WithUserPermissions(GroupLabelOwners);

/*
 * It is worthwhile to make a unique HeaderRow rather than as part of a standard xRow component because
 * - It may respond differently to other states
 * - It requires specific classes,
 * - It wil for the most part not share Cells with the other rows
 * */
const HeaderRow = ({ details }) => {
  const className = classnames({
    'header-row': true,
    'row-parent': true,
    [details.name.groupingClassName]: true,
    'row-brokerage':
      isBrokerage(details.productType) || isActivePlus(details.productType)
  });

  const name = details.name.groupLabel.accountType || details.name.groupLabel;
  const owners = details.name.groupLabel.owners;
  /*
   * There is no need for the headerCellMapper in this case because the cells are not changing
   * and there aren't many cells to display
   * Down the road we may have additional possible states for the Header.
   * If the possible states are mutually exclusive ( e.g. MD | AK | 529Only | _ ) then this could us <Optioner>
   * If it turns out we need to display more cells in general then a mapper function would become appropriate.
   */
  return (
    <tr className={className}>
      <td key={name} className="mobile-dashboard-table-account-group-label">
        {name}
        <GroupLabelOwnersWithPermissions owners={owners} shouldMask />
      </td>
      <Dollar
        element="td"
        className="mobile-dashboard-table-account-name-value">
        {details.marketValue}
      </Dollar>
    </tr>
  );
};

HeaderRow.propTypes = {
  details: object,
  children: array
};

const RefreshRow = ({ accounts }) => {
  const intradayEnabled = intradayRefreshEnabled();
  if (
    !intradayEnabled &&
    !accounts.some((account) => isBrokerage(account.details.productType))
  ) {
    return null;
  }

  return (
    <tr className="mobile-dashboard-table-row detail-header-row">
      <td colSpan={2} className="mobile-dashboard-table-account-name-value">
        <IntradayRefresh containerClassName="intraday-refreshing-label" />
      </td>
    </tr>
  );
};

RefreshRow.propTypes = {
  accounts: array
};

const RowLabelCell = ({ children, className }) =>
  isString(children) ? <td className={className}>{children}</td> : children;

// fullDetails included for inevitable AccountDetail checks
const rowLabelClass = (name) =>
  `mobile-dashboard-table-row-label e2e-mobile-dashboard-table-row-label ${name}-row-label e2e-${name}-row-label`;
const rowValueClass = (name) =>
  `mobile-dashboard-table-row-value e2e-mobile-dashboard-table-row-value ${name}-row-value e2e-${name}-row-value`;

const DetailRow = ({
  value,
  name,
  type,
  fullDetails,
  isHoldingsRow = false
}) => {
  const labelMapper = labelCells({
    cellLabelClass: rowLabelClass(name),
    type,
    fullDetails
  });

  const cellMapper = valueCells({
    cellValueClass: rowValueClass(name),
    type
  });

  const rowClassName = `mobile-dashboard-table-${
    isHoldingsRow ? 'holdings-' : ''
  }row e2e-${name}-row`;

  return (
    <tr key={name} className={rowClassName}>
      <RowLabelCell className={rowLabelClass(name)}>
        {(labelMapper[name] || labelMapper.default)(name, value)}
      </RowLabelCell>
      {(cellMapper[name] || cellMapper.default)(value, fullDetails)}
    </tr>
  );
};

DetailRow.propTypes = {
  value: oneOfType([string, number]),
  name: string,
  fullDetails: object
};

const rowDataWhiteList = ['ownerNames'];

const mapDetailsToRows = (fullDetails, metaData) => (acc, value, key) =>
  metaData[key] === metaDataTypes.HIDE
    ? acc
    : isPlainObject(value) && !rowDataWhiteList.includes(key)
    ? acc.concat(DetailCollection({ details: value, metaData }))
    : acc.concat(
        DetailRow({
          value,
          name: key,
          fullDetails,
          type: metaData[key]
        })
      );

const mapDetailsToHoldingsRows = (fullDetails, metaData) => (acc, value, key) =>
  metaData[key] === metaDataTypes.HIDE
    ? acc
    : isPlainObject(value) && !rowDataWhiteList.includes(key)
    ? acc.concat(HoldingsDetailCollection({ details: value, metaData }))
    : acc.concat(
        DetailRow({
          value,
          name: key,
          fullDetails,
          type: metaData[key],
          isHoldingsRow: true
        })
      );

const detailBlackList = [
  '_type',
  'accountName',
  'accountChecksum',
  'accountNumber',
  'accountTypeId',
  'trpCode',
  'hiddenAccount',
  'isViewOnly',
  'ownershipType',
  'moneyMarket',
  'productType',
  'owner',
  'ownerNames',
  'availableBalance',
  'isHolding',
  'isCashRow',
  'cusipId',
  'quantity',
  'price',
  'change',
  'actions',
  'sweepAccount',
  'isClosed'
];

const DetailCollection = ({ details, mmrMessage, metaData }) => {
  const rows = flow(
    (d) => omit(d, detailBlackList),
    (d) => omitBy(d, isNil),
    (d) => orderAccountTableKeys(d),
    (d) => reduce(d, mapDetailsToRows(details, metaData), []) // `details` is needed in case a row requires additional external information
  )(details);

  if (mmrMessage) {
    const mmrMessageHtml = `${mmrMessage} For questions, please call T. Rowe Price at 877-218-5162.`;
    rows.push(
      <tr className="mobile-dashboard-table-row mmr-message-row">
        <td
          colSpan={2}
          dangerouslySetInnerHTML={{ __html: mmrMessageHtml }}></td>
      </tr>
    );
  }

  return rows;
};

const HoldingsDetailCollection = ({ details, metaData }) =>
  flow(
    (d) => omit(d, detailBlackList),
    (d) => orderAccountTableKeys(d),
    (d) => reduce(d, mapDetailsToHoldingsRows(details, metaData), []) // `details` is needed in case a row requires additional external information
  )(details);

const AccountHoldings = ({ holdings = [] }) => (
  <tr className="mobile-dashboard-table-holdings-containing-row">
    <td colSpan={2} className="mobile-dashboard-table-holdings-containing-data">
      {holdings.map((h, i) => (
        <div
          className="mobile-dashboard-table-holdings-table-container"
          key={i}>
          <table className="mobile-dashboard-table">
            <tbody>
              <tr className="mobile-dashboard-table-holdings-row">
                <td
                  colSpan={2}
                  className="mobile-dashboard-table-row-label account-name-row-lab">
                  {h.details.name.accountName}
                </td>
              </tr>
              <HoldingsDetailCollection {...h} />
            </tbody>
          </table>
        </div>
      ))}
    </td>
  </tr>
);

const AccountDetailsHeaderRow = ({ toggle, on, details }) => (
  <tr className="mobile-dashboard-table-row detail-header-row" onClick={toggle}>
    <td
      colSpan={on ? 2 : 1}
      className={`mobile-dashboard-table-account-name-label ${
        on ? 'expanded' : ''
      }`}>
      {details.name.accountName}
    </td>
    {!on && (
      <Dollar
        element="td"
        className="mobile-dashboard-table-account-name-value">
        {details.marketValue}
      </Dollar>
    )}
  </tr>
);

const AccountRows = ({ accounts }) =>
  accounts.reduce(
    (acc, rowObj, i) => [
      ...acc,
      <Toggle key={'toggle' + i}>
        {({ on, toggle }) => (
          <Fragment>
            <AccountDetailsHeaderRow
              key={i}
              toggle={toggle}
              on={on}
              {...rowObj}
            />
            {on && (
              <Fragment>
                <AccountLabel accountId={rowObj.details.name.accountId} />
              <DetailCollection key={'details' + i} {...rowObj} />
              {rowObj.metaData.holdings === metaDataTypes.SHOW && (
                <AccountHoldings holdings={rowObj.children} />
              )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Toggle>
    ],
    []
  );

export const MobileDashboardTable = ({
  columns,
  groups,
  showIntradayRefresh
}) => {
  const organizedGroups = sortBy(
    sortBy(groups, getTypeName),
    (g) => g.details.name.retirementAccount
  );

  return (
    <div className="mobile-table-container">
      {organizedGroups.map((group, i) => {
        const intradayEligibleAccounts = group.children
          .map((c) => c.details)
          .filter((detail) => intradayEligibleFilter(detail));
        return (
          <table key={i} className="mobile-dashboard-table">
            <thead>
              <TableColumns columns={columns} />
            </thead>
            <tbody>
              <HeaderRow {...group} />
              {showIntradayRefresh && intradayEligibleAccounts.length > 0 && (
                <RefreshRow accounts={group.children} />
              )}
              <AccountRows accounts={group.children} />
            </tbody>
          </table>
        );
      })}
    </div>
  );
};

MobileDashboardTable.propTypes = {
  groups: array,
  is529Grouping: bool,
  accountHistoricalDate: string,
  hasAlaska529Only: bool,
  hasMaryland529Only: bool,
  hasCollegeSavingsOnly: bool
};

const mapStateToProps = (state) => {
  const { accountOverviewTable, view } = state;
  return {
    groups: accountOverviewTable,
    showIntradayRefresh: getIntradayRefreshEnabled(state),
    view,
    columns: defaultColumns
  };
};

export default withRouter(connect(mapStateToProps)(MobileDashboardTable));
// TODO make a settable value for colSpan
